import * as React from 'react'
import {
    Link,
    Router,
    useLocation,
    useNavigate,
    useNavigationType,
    useRoutes,
} from 'react-router-dom'
import { Button } from 'primereact'
import { TabMenu } from 'primereact/tabmenu'
import { MenuItem } from 'primereact/menuitem'
import Icon from 'app/shared/components/Icon'
import { useDialogManager } from 'app/shared/hooks/dialogManager.hook'
import { useAppSelector } from 'app/core/hooks'
import {
    selectorMenuDic,
    selectorMenuItems,
} from 'app/core/slices/user/menu.slice'
import useBasePage from 'app/shared/hooks/base-page.hook'
import {
    setProductMainArr,
    clearProductMain,
} from 'app/core/slices/product/productMain.slice'
import { useDispatch } from 'react-redux'

import { useLazySelectComUrl0102Query } from 'app/core/services/com/com.service'
import { waitFor } from '@testing-library/react'
import { convertToHtml } from 'app/shared/utils/comUtil'
import { useProductJoin } from 'app/shared/hooks/util/product.hook'

/*======================================================================
 * @ 업무설명 : 전체메뉴
 * @ 파 일 명 : /app/pages/etc/mnu/ETCMNU0101.tsx
 * @ 관련API  : com0102  - router에서 호출
 * @ 파일설명 : 메뉴 data - store 에서 useAppSelector(selectorMenuItems) 호출
 *======================================================================
 * 번호  작업자           작업일         변경내용
 *======================================================================
 * 1     한중근(S27239)   2025.06.23    최초작성
 *======================================================================
 */

type props = {
    open: boolean
    onClose?(): void
}

const ETCMNU0101: React.FC<props> = ({ open = false, onClose }) => {
    const dispatch = useDispatch()
    const [naviHandle, setNaviHandle] = React.useState(open)
    const router = useBasePage()
    useDialogManager(naviHandle)
    const navigationBtn = 'navigationBtn'
    const headerRef = React.useRef<HTMLDivElement>(null)
    const menuList = useAppSelector(selectorMenuItems) ?? []
    const menuDic = useAppSelector(selectorMenuDic) ?? []

    const { user } = useProductJoin()

    const chatBotUrl = React.useRef<string>('')

    // 챗봇용
    const [selectComNoit0102] = useLazySelectComUrl0102Query()

    React.useEffect(() => {
        setNaviHandle(open)
    }, [open])

    React.useEffect(() => {
        setTimeout(() => {
            if (naviHandle && headerRef.current) headerRef.current.focus()
        })
    }, [naviHandle])

    const naviClose = () => {
        setNaviHandle(false)
        document.getElementById(`${navigationBtn}`)?.focus()
        onClose?.()
    }

    React.useEffect(() => {
        init()
    }, [])

    const init = async () => {
        const result001 = await selectComNoit0102({
            dsrnTgtBacd: '110',
            cdgpId: 'EXTL_LURL',
            ccddId: 'LINK_004',
        })

        if (result001?.isSuccess) {
            chatBotUrl.current = convertToHtml(
                result001?.data?.data['cdAtrbEtc4Con']
            )
        }
    }

    const tabItems: MenuItem[] = [
        {
            label: '상품몰',
            template: (item, options) => (
                <Button
                    className="p-menuitem-link"
                    label={item.label}
                    onClick={options.onClick}
                />
            ),
        },
        {
            label: '금융서비스',
            template: (item, options) => (
                <Button
                    className="p-menuitem-link"
                    label={item.label}
                    onClick={options.onClick}
                />
            ),
        },
        {
            label: '이벤트',
            template: (item, options) => (
                <Button
                    className="p-menuitem-link"
                    label={item.label}
                    onClick={options.onClick}
                />
            ),
        },
        {
            label: '고객지원',
            template: (item, options) => (
                <Button
                    className="p-menuitem-link"
                    label={item.label}
                    onClick={options.onClick}
                />
            ),
        },
    ]

    // tab
    const [tabActive, setTabActive] = React.useState(0)
    const productRef = React.useRef<HTMLDivElement>(null)
    const serviceRef = React.useRef<HTMLDivElement>(null)
    const eventRef = React.useRef<HTMLDivElement>(null)
    const supportRef = React.useRef<HTMLDivElement>(null)
    const sectionRefs = [productRef, serviceRef, eventRef, supportRef]
    const refDict = new Map<string, React.MutableRefObject<HTMLDivElement>>([
        ['M0002', productRef],
        ['M0003', serviceRef],
        ['M0004', eventRef],
        ['M0005', supportRef],
    ])
    const tabHandle = (e) => {
        setTabActive(e.index)
        const targetRef = sectionRefs[e.index]
        if (targetRef && targetRef.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth' })
        }
    }
    //Force Rerendering----------------------------------------------
    const [currentPage, setCurrentPage] = React.useState('')
    const locate = useLocation()
    const navType = useNavigationType()

    React.useEffect(() => {
        //화면 변동 시마다 네비를 닫는다. 이것도 정답이 아님.
        return naviClose()
    }, [locate.key])

    const onClickMenu = (url: string, menuId: string) => {
        dispatch(clearProductMain())
        //상품몰
        if (menuId === 'M0002') {
            const catlDcd =
                menuDic['M0002']?.menuUrlAdr?.split('?')[1]?.split('=')[1] ??
                '103'

            dispatch(
                setProductMainArr([
                    { key: 'catlDcd', value: catlDcd },
                    { key: 'sortNam', value: '추천순' },
                    { key: 'tabviewAllYn', value: false },
                ])
            )
        }
        /**
         * 상품 카테고리코드
         * 입출금 : '103'
            적금∙청약 : '101'
            예금∙중금채 : '102'
            체크카드 : '302'
            신용카드 : '301'
            대출 : '600'
            보험 : '400'
            퇴직연금(IRP) : 'A00'
            ISA : '800'
            펀드 : '500'
        */
        router.runNetFunnel(url, menuId)
    }

    return (
        <aside id="navigation" className={naviHandle === true ? 'open' : ''}>
            <header ref={headerRef} tabIndex={-1} className="header-menu">
                <Link
                    to="/M0000"
                    className="btn-home"
                    onClick={(e) => {
                        e.preventDefault()
                        onClickMenu('', 'M0000')
                    }}
                >
                    <Icon name="home" text="메인으로 이동" />
                </Link>
                <Button
                    className="btn-chatbot"
                    onClick={() => {
                        window.open(chatBotUrl.current, '_blank')
                    }}
                >
                    <Icon name="chatbot" text="챗봇 화면으로 이동" />
                </Button>

                <Button className="btn-close" onClick={naviClose}>
                    <Icon name="x2" text="전체메뉴 닫기"></Icon>
                </Button>
            </header>
            <div className="navigation-content">
                <div className="top-menu">
                    <Link
                        to="/M0002"
                        className="btn"
                        onClick={(e) => {
                            e.preventDefault()
                            onClickMenu('', 'M0002')
                        }}
                    >
                        <Icon name="gift2" text="상품몰 화면 이동" />
                        상품몰
                    </Link>
                    <Link
                        to=""
                        className="btn"
                        onClick={(e) => {
                            e.preventDefault()
                            onClickMenu('/etc/sts/ETCSTS0101', '')
                        }}
                    >
                        <Icon name="note" text="신청내역 화면 이동" />
                        신청내역
                    </Link>
                    <Link
                        to="/M0021"
                        className="btn"
                        onClick={(e) => {
                            e.preventDefault()
                            onClickMenu('', 'M0021')
                        }}
                    >
                        <Icon name="headphone" text="고객지원 화면 이동" />
                        고객지원
                    </Link>
                </div>
                {user.cusInfo.alplDcd !== '01' ? (
                    // {locate.pathname !== `/mai/bob/MAIBOB0101` ? (
                    <div className="input-basic -search">
                        <Button
                            title="지점찾기 이동"
                            className="input"
                            onClick={() => router.goPage('/etc/brc/ETCBRC0101')}
                        >
                            <Icon name="search" text="검색"></Icon>
                            찾으시는 지점을 입력하세요
                        </Button>
                    </div>
                ) : (
                    <></>
                )}

                <div className="tabs-wrap">
                    <TabMenu
                        className="tabs-tags -reel"
                        model={tabItems}
                        activeIndex={tabActive}
                        onTabChange={tabHandle}
                    />
                </div>

                <div className="sub-menu">
                    {menuList.map((item, idx) => {
                        if (refDict.has(item.menuId)) {
                            const ref = refDict.get(item.menuId)
                            return (
                                <h2 key={item.menuId} ref={ref} className="h5">
                                    {item.menuNm}
                                </h2>
                            )
                        } else if (
                            item.menuLvlSqn === '3' &&
                            item.menuTcd === '01' &&
                            item.useYn === 'Y'
                        ) {
                            return (
                                <ul key={item.menuId}>
                                    <li>
                                        <Link
                                            // to={item.menuUrlAdr}
                                            to={''}
                                            onClick={(e) => {
                                                e.preventDefault()

                                                onClickMenu(
                                                    item.menuUrlAdr,
                                                    item.menuId
                                                )
                                            }}
                                        >
                                            {item.menuNm}
                                        </Link>
                                    </li>
                                </ul>
                            )
                        }
                    })}
                </div>
            </div>
        </aside>
    )
}

export default ETCMNU0101
